/**
 * The Translations page's reads and writes, and the small formatting it shares
 * (`features/translations`). The API is admin-only: `/api/admin/translations`.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  providerLabel,
  translationLanguage,
  type TranslationActivity,
  type TranslationDeliveryQueued,
  type TranslationDeliveryRequest,
  type TranslationJobErrorView,
  type TranslationPerformance,
  type TranslationFile,
  type TranslationSettings,
  type TranslationsOverview,
  type TranslationVariantDetail,
} from "@scandi/shared";
import { request } from "@/lib/api";

const BASE = "/api/admin/translations";
export const overviewKey = ["translations", "overview"] as const;
export const variantKey = (slug: string) => ["translations", "variant", slug] as const;
export const activityKey = ["translations", "activity"] as const;

/** How often the worker's state is asked for: often while it works, rarely when it rests. */
const LIVE_MS = 4_000;
const IDLE_MS = 30_000;

/**
 * What the cloud worker is doing with translations. Polls every few seconds
 * while anything is queued or running and every half minute otherwise;
 * react-query stops while the tab is hidden. `slow` is for watchers that only
 * need a count (the sidebar).
 */
export function useTranslationActivity({ enabled = true, slow = false }: { enabled?: boolean; slow?: boolean } = {}) {
  return useQuery({
    queryKey: activityKey,
    queryFn: () => request<TranslationActivity>(`${BASE}/activity`),
    enabled,
    staleTime: 2_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      const busy = data ? data.running + data.queued > 0 : false;
      return slow ? (busy ? 10_000 : 60_000) : busy ? LIVE_MS : IDLE_MS;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * When the set of jobs in flight changes - one finished, one appeared - the
 * library and the open hook are read again, so a finished language turns into
 * its player and a new concept joins the list without a reload.
 */
export function useActivitySync(activity: TranslationActivity | undefined) {
  const queryClient = useQueryClient();
  const last = useRef<string | null>(null);
  const signature = activity ? [...activity.jobs.map((j) => j.id)].sort((a, b) => a - b).join(",") + "|" + activity.attention.map((f) => f.id).join(",") : null;
  useEffect(() => {
    if (signature === null) return;
    if (last.current !== null && last.current !== signature) {
      void queryClient.invalidateQueries({ queryKey: overviewKey });
      void queryClient.invalidateQueries({ queryKey: ["translations", "variant"] });
    }
    last.current = signature;
  }, [signature, queryClient]);
}

/** Meta's numbers move with the hourly sync: reading them more often returns the same rows. */
const PERFORMANCE_MS = 5 * 60 * 1000;

/**
 * Spend and ROAS of the translated ads over a range (null bounds: all time),
 * per language and hook. Not polled with the library: it changes hourly, and
 * the previous range's numbers stay up while a new one loads.
 */
export function useTranslationPerformance(from: string | null, to: string | null) {
  return useQuery({
    queryKey: ["translations", "performance", from, to] as const,
    queryFn: () => {
      const query = new URLSearchParams();
      if (from) query.set("from", from);
      if (to) query.set("to", to);
      const qs = query.toString();
      return request<TranslationPerformance>(`${BASE}/performance${qs ? `?${qs}` : ""}`);
    },
    staleTime: PERFORMANCE_MS,
    refetchInterval: PERFORMANCE_MS,
    placeholderData: (previous) => previous,
  });
}

/** Read again every few seconds while syncs to Drive and the sheet are on their way, so they land without a reload. */
const DELIVERY_LIVE_MS = 5_000;

export function useTranslationsOverview() {
  return useQuery({
    queryKey: overviewKey,
    queryFn: () => request<TranslationsOverview>(BASE),
    staleTime: 30_000,
    refetchInterval: (query) => ((query.state.data?.delivery?.active ?? 0) > 0 ? DELIVERY_LIVE_MS : false),
    refetchIntervalInBackground: false,
  });
}

export function useTranslationVariant(slug: string | null) {
  return useQuery({
    queryKey: variantKey(slug ?? ""),
    queryFn: () => request<TranslationVariantDetail>(`${BASE}/variants/${encodeURIComponent(slug!)}`),
    enabled: slug !== null,
    staleTime: 30_000,
    retry: false,
    refetchInterval: (query) => {
      const syncing = query.state.data?.languages.some((l) => l.delivery?.job && l.delivery.job.status !== "failed");
      return syncing ? DELIVERY_LIVE_MS : false;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * Upload to Drive and add to the languages sheet: one translation
 * (`{variantKey, language}`), or every pinned-language one not there yet
 * (`{all: true}`). The library, the worker's state and the open hook are read
 * again after it.
 */
export function useDeliver() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TranslationDeliveryRequest) =>
      request<TranslationDeliveryQueued>(`${BASE}/deliveries`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: overviewKey });
      void queryClient.invalidateQueries({ queryKey: activityKey });
      void queryClient.invalidateQueries({ queryKey: ["translations", "variant"] });
    },
  });
}

/** A delivery step in the reader's words ("Uploading to Drive"); an unknown one as the server named it. */
export function useDeliveryStepLabel(): (step: string | null) => string {
  const { t, i18n } = useTranslation();
  return useCallback(
    (step) => {
      if (!step) return t("translations.delivery.syncing");
      const key = `translations.delivery.steps.${step}`;
      return i18n.exists(key) ? t(key as "translations.delivery.syncing") : step;
    },
    [t, i18n],
  );
}

export function useArchive(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: number; archived: boolean }) =>
      request<{ translation: TranslationFile }>(`${BASE}/files/${id}/archive`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ archived }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: variantKey(slug) });
      void queryClient.invalidateQueries({ queryKey: overviewKey });
    },
  });
}

export function useSavePins() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pinned: string[]) =>
      request<TranslationSettings>(`${BASE}/settings`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pinned }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["translations"] });
    },
  });
}

/** The reader's locale for `Intl`, as the rest of the app picks it. */
export function useLocale(): string {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage === "es" ? "es-419" : "en-GB";
}

/**
 * A language's name in the reader's language ("German", "alemán" ->
 * "Alemán"), from the browser's own table, falling back to the catalogue's
 * English name and then to the tag itself.
 */
export function useLanguageName(): (code: string) => string {
  const locale = useLocale();
  const names = useMemo(() => {
    try {
      return new Intl.DisplayNames([locale], { type: "language", languageDisplay: "standard" });
    } catch {
      return null;
    }
  }, [locale]);
  return useCallback(
    (code: string) => {
      let name: string | undefined;
      try {
        name = names?.of(code);
      } catch {
        name = undefined;
      }
      if (!name || name === code) name = translationLanguage(code)?.name ?? code;
      return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
    },
    [names, locale],
  );
}

/** "1:32", "0:07". */
export function formatLength(ms: number): string {
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** "48.2 MB", "820 KB", with a non-breaking space so the unit never wraps away from its number. */
export function formatBytes(bytes: number, locale: string): string {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const digits = unit >= 2 && value < 100 ? 1 : 0;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value)}\u00A0${units[unit]}`;
}

/** "9 Oct, 14:02" (with the year when it is not this one). */
export function useWhen(): (iso: string | null) => string {
  const locale = useLocale();
  return useCallback(
    (iso) => {
      if (!iso) return "";
      const date = new Date(iso);
      const sameYear = date.getFullYear() === new Date().getFullYear();
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        ...(sameYear ? {} : { year: "numeric" }),
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    [locale],
  );
}

/** "Hook 2", "Hook 2 · ITR 1", "No hook": a hook as the reader names it; `short` is the compact "H2 · ITR1". */
export function useHookLabel(): (h: { hook: number | null; iteration: number | null }, short?: boolean) => string {
  const { t } = useTranslation();
  return useCallback(
    (h, short = false) => {
      const hook = h.hook === null ? t("translations.hook.none") : t(short ? "translations.hook.short" : "translations.hook.long", { n: h.hook });
      return h.iteration === null ? hook : `${hook} · ${t(short ? "translations.hook.iterationShort" : "translations.hook.iteration", { n: h.iteration })}`;
    },
    [t],
  );
}

/** The worker's step in the reader's words ("Dubbing", "Rendering"); an unknown step as the worker named it. */
export function useStepLabel(): (step: string | null) => string {
  const { t, i18n } = useTranslation();
  return useCallback(
    (step) => {
      if (!step) return t("translations.live.running");
      const key = `translations.live.steps.${step}`;
      return i18n.exists(key) ? t(key as "translations.live.running") : step;
    },
    [t, i18n],
  );
}

/* ------------------------------------------------------------ job actions */

const SUBMIT = `${BASE}/submit`;

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "POST", headers: { "content-type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) });
}

/** Retry now / Cancel, one job or many; Try now on a paused kind. The activity is read again after each. */
export function useJobActions() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: activityKey });
    void queryClient.invalidateQueries({ queryKey: overviewKey });
  };
  const retry = useMutation({ mutationFn: (ids: number[]) => post<{ retried: number[] }>(`${SUBMIT}/jobs/retry`, { ids }), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: (ids: number[]) => post<{ cancelled: number; stopping: number }>(`${SUBMIT}/jobs/cancel`, { ids }), onSuccess: refresh });
  const tryNow = useMutation({ mutationFn: (kind: string) => post<{ ok: boolean; requeued: number[] }>(`${SUBMIT}/kinds/${encodeURIComponent(kind)}/try-now`), onSuccess: refresh });
  return { retry, cancel, tryNow };
}

/** "15:40", or "Tue 15:40" when it is not today. */
export function useClock(): (iso: string | null) => string {
  const locale = useLocale();
  return useCallback(
    (iso) => {
      if (!iso) return "";
      const date = new Date(iso);
      const today = date.toDateString() === new Date().toDateString();
      return new Intl.DateTimeFormat(locale, { ...(today ? {} : { weekday: "short" }), hour: "2-digit", minute: "2-digit" }).format(date);
    },
    [locale],
  );
}

/** A job's error in plain words ("ElevenLabs is out of credits."), from its code; an unknown one as the worker said it. */
export function useErrorText(): (error: TranslationJobErrorView | null | undefined) => string {
  const { t, i18n } = useTranslation();
  return useCallback(
    (error) => {
      if (!error) return t("translations.live.noReason");
      const key = `translations.errors.${error.code}`;
      const provider = error.provider ? providerLabel(error.provider) : t("translations.errors.theProvider");
      if (error.code === "unknown" || !i18n.exists(key)) return error.detail || t("translations.live.noReason");
      return t(key as "translations.live.noReason", { provider });
    },
    [t, i18n],
  );
}
